import { fromB64Url, isHex64, toB64Url } from "./crypto.js";

// Relay URL shape accepted anywhere a relay can be configured (invites, the
// create-group form, group settings). One definition so the layers agree.
export function isValidRelayUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol === "https:") return true;
  // Plain http is allowed only for a local dev relay; in production the HTTPS
  // app would block it as mixed content, and it would put envelope metadata
  // (sizes, timing, topic) on the wire in the clear.
  return parsed.protocol === "http:" && (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1");
}

// Invites travel in the URL FRAGMENT (`https://app/#...`) or a QR code. The
// fragment never leaves the browser - it is not sent in HTTP requests - so the
// group key stays out of every server log by construction.

export interface Invite {
  groupId: string;
  key: Uint8Array; // 32-byte group key for `epoch`
  epoch: number; // which key this is; 0 is the founding key
  relayUrl: string; // "" when the group is QR-sync only
  name: string; // group display name, for the join screen
}

export function buildInviteFragment(invite: Invite): string {
  const params = new URLSearchParams();
  params.set("ps", "1");
  params.set("g", invite.groupId);
  params.set("k", toB64Url(invite.key));
  // Omitted on the founding key, so an epoch-0 invite is byte-identical to the
  // ones already in circulation.
  if (invite.epoch !== 0) params.set("e", String(invite.epoch));
  if (invite.relayUrl !== "") params.set("r", invite.relayUrl);
  if (invite.name !== "") params.set("n", invite.name);
  return params.toString();
}

export function parseInviteFragment(fragment: string): Invite | null {
  const raw = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(raw);
  } catch {
    return null;
  }
  if (params.get("ps") !== "1") return null;
  const groupId = params.get("g") ?? "";
  if (!isHex64(groupId)) return null;
  let key: Uint8Array;
  try {
    key = fromB64Url(params.get("k") ?? "");
  } catch {
    return null;
  }
  if (key.length !== 32) return null;
  const rawEpoch = params.get("e");
  let epoch = 0;
  if (rawEpoch !== null) {
    epoch = Number(rawEpoch);
    if (!Number.isSafeInteger(epoch) || epoch < 0) return null;
  }
  const relayUrl = params.get("r") ?? "";
  if (relayUrl !== "" && !isValidRelayUrl(relayUrl)) return null;
  const name = params.get("n") ?? "";
  if (name.length > 64) return null;
  return { groupId, key, epoch, relayUrl, name };
}
